// framework/client/components/DataForm.js

import { ComponentBase } from "../core/ComponentBase.js";
function getValueByPath(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function setValueByPath(obj, path, value) {
  const keys = path.split('.');
  let current = obj;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
  });
}

export class DataForm extends ComponentBase {
  constructor(config) {
    super(config);
    this.data = null;
    this.externalData = {};

    this.errors = {};
    this.isDirty = false;
    this.isValid = true;
    this.loading = false;
    // To track custom components (including array containers)
    this.fieldComponents = {};

    this._debouncedHandleFormChange = debounce(this._handleFormChange.bind(this), 50);

    // Reference to the rendered form element; used for conditional visibility and validation-on change.
    this.formElement = null;

    this.isModalVisible = false;

  }

  static getDefaultConfig() {
    return {
      mode: 'edit', // "edit", "display", "filter"
      layout: 'standard', // "standard", "propertyGrid", "dialog"
      columns: 1,
      schema: null,
      // Fields can also be nested by using dot‑notation or an array field (type: "array")
      fields: {},
      sections: null,
      containerClass: 'card h-100',
      bodyClass: 'card-body',
      footerClass: 'card-footer',
      actions: { save: true, cancel: true },
      autoLoad: false,
      dataProvider: null,
      context: null,
      hideHeader: false,
      autoSave: false,
      // Validation options – if validateOnChange is true, validation will run on every change event.
      validation: {
        validateOnChange: false,
        showErrors: true
      }
    };
  }

  styles = `
    /* ============================
       BASE FORM LAYOUT
       ============================ */
    .data-form {
      display: flex;
      flex-direction: column;
      /* No max-height here — container controls sizing */
      height: 100%;
    }

    /* ============================
       DIALOG BACKDROP & CONTAINER
       ============================ */
    .dialog-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: var(--bs-backdrop-bg, rgba(15, 23, 42, 0.45));
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: var(--ab-z-backdrop);
      animation: ab-fade-in 0.2s ease-out;
    }

    .dialog-container {
      background: var(--bs-body-bg);
      border-radius: var(--ab-radius-lg);
      border: 1px solid var(--bs-border-color-translucent);
      box-shadow: var(--ab-shadow-xl);
      width: calc(100% - 2rem);
      max-width: 600px;
      margin: 1rem;
      position: relative;
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      animation: ab-scale-up 0.25s cubic-bezier(0.34, 1.4, 0.64, 1);
    }

    @keyframes ab-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes ab-scale-up {
      from { transform: scale(0.96) translateY(8px); opacity: 0; }
      to { transform: scale(1) translateY(0); opacity: 1; }
    }

    .dialog-container > form,
    .dialog-container > div {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    /* ============================
       CARD HEADER
       ============================ */
    .card-header {
      background: linear-gradient(
        135deg,
        var(--bs-tertiary-bg) 0%,
        var(--bs-body-bg) 100%
      );
      border-bottom: 1px solid var(--bs-border-color);
      padding: 1rem 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .card-header h5 {
      font-size: var(--ab-text-md);
      font-weight: var(--ab-weight-semibold);
      margin: 0;
      color: var(--bs-emphasis-color);
      letter-spacing: -0.01em;
    }

    .btn-close {
      padding: 0.5rem;
      opacity: 0.55;
      transition: opacity var(--ab-t-fast) ease, transform var(--ab-t-fast) ease;
    }
    .btn-close:hover {
      opacity: 1;
      transform: rotate(90deg);
    }
    .btn-close:focus-visible {
      outline: none;
      box-shadow: var(--ab-focus-ring);
    }

    .dialog-container .card-header {
      border-top-left-radius: var(--ab-radius-lg);
      border-top-right-radius: var(--ab-radius-lg);
      flex-shrink: 0;
    }

    /* ============================
       FORM BODY
       ============================ */
    .data-form-body {
      flex: 1;
      overflow-y: auto;
      padding: 1.25rem 1.5rem;
      scrollbar-width: thin;
      scrollbar-color: var(--bs-border-color-translucent) transparent;
    }

    /* ============================
       CARD FOOTER (Action buttons)
       ============================ */
    .dialog-container .card-footer,
    .card-footer {
      flex-shrink: 0;
      padding: 0.875rem 1.25rem;
      border-top: 1px solid var(--bs-border-color);
      background: var(--bs-tertiary-bg);
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    .dialog-container .card-footer {
      border-bottom-left-radius: var(--ab-radius-lg);
      border-bottom-right-radius: var(--ab-radius-lg);
    }

    /* Primary button in footer */
    .card-footer .btn-primary {
      padding: var(--ab-btn-pad);
      font-weight: var(--ab-weight-semibold);
      font-size: var(--ab-text-sm);
      border-radius: var(--ab-radius-sm);
      box-shadow: 0 1px 3px rgba(var(--bs-primary-rgb), 0.3);
      transition: background-color var(--ab-t-fast), box-shadow var(--ab-t-fast), transform var(--ab-t-fast);
    }
    @media (hover: hover) {
      .card-footer .btn-primary:hover {
        transform: var(--ab-lift-sm);
        box-shadow: 0 4px 8px rgba(var(--bs-primary-rgb), 0.35);
      }
    }
    .card-footer .btn-primary:focus-visible {
      outline: none;
      box-shadow: var(--ab-focus-ring);
    }

    .card-footer .btn-secondary {
      padding: var(--ab-btn-pad);
      font-size: var(--ab-text-sm);
      border-radius: var(--ab-radius-sm);
      transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
    }
    .card-footer .btn-secondary:focus-visible {
      outline: none;
      box-shadow: var(--ab-focus-ring);
    }

    /* ============================
       FORM GRID & SECTIONS
       ============================ */
    .form-grid {
      display: grid;
      grid-template-columns: repeat(var(--form-columns, 1), 1fr);
      gap: 1.1rem;
    }

    .form-section {
      margin-bottom: 1.5rem;
    }

    .form-section-title {
      font-size: var(--ab-label-size);
      font-weight: var(--ab-label-weight);
      color: var(--ab-label-color);
      text-transform: uppercase;
      letter-spacing: var(--ab-label-spacing);
      margin-bottom: 1rem;
      padding-bottom: 0.4rem;
      border-bottom: 2px solid var(--bs-primary-bg-subtle);
    }

    /* ============================
       FIELD CONTAINERS
       ============================ */
    .field-container {
      margin-bottom: 0.25rem;
      transition: all var(--ab-t-fast) ease;
    }

    .field-container.wiggle-error {
      animation: ab-wiggle 0.4s ease;
    }

    @keyframes ab-wiggle {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-4px); }
      40%, 80% { transform: translateX(4px); }
    }

    .field-label {
      font-size: var(--ab-text-xs);
      font-weight: var(--ab-weight-semibold);
      margin-bottom: 0.3rem;
      color: var(--bs-secondary-color);
      letter-spacing: 0.01em;
    }

    .field-error {
      color: var(--bs-danger);
      font-size: var(--ab-text-xs);
      margin-top: 0.2rem;
      font-weight: var(--ab-weight-medium);
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .required-indicator {
      color: var(--bs-danger);
      margin-left: 0.2rem;
    }

    /* ============================
       DISPLAY MODE (view)
       ============================ */
    .display-mode .field-container {
      border-bottom: 1px solid var(--bs-border-color-subtle);
      padding: 0.6rem 0;
      margin-bottom: 0;
    }

    .display-mode .field-container:last-child {
      border-bottom: none;
    }

    .display-mode .field-label {
      font-size: var(--ab-text-2xs);
      font-weight: var(--ab-weight-semibold);
      text-transform: uppercase;
      letter-spacing: var(--ab-label-spacing);
      color: var(--bs-secondary-color);
      margin-bottom: 0.2rem;
    }

    .display-mode .field-value {
      font-size: var(--ab-text-base);
      font-weight: var(--ab-weight-medium);
      color: var(--bs-emphasis-color);
      line-height: 1.4;
    }

    /* Display mode card wrapper */
    .display-mode .data-form-body {
      padding: 0.25rem 1.5rem;
    }

    /* ============================
       INPUT STATES
       ============================ */
    .data-form input.form-control,
    .data-form select.form-select,
    .data-form textarea.form-control {
      border-radius: var(--ab-radius-sm);
      border: 1.5px solid var(--bs-border-color);
      font-size: var(--ab-text-base);
      background-color: var(--bs-body-bg);
      transition: border-color var(--ab-t-base) ease, box-shadow var(--ab-t-base) ease;
    }

    .data-form input.form-control:focus,
    .data-form select.form-select:focus,
    .data-form textarea.form-control:focus {
      border-color: var(--ab-focus-border);
      box-shadow: var(--ab-focus-ring);
      outline: none;
    }

    .data-form input.form-control.is-invalid,
    .data-form select.form-select.is-invalid,
    .data-form textarea.form-control.is-invalid {
      border-color: var(--bs-danger) !important;
    }

    .data-form input.form-control.is-invalid:focus,
    .data-form select.form-select.is-invalid:focus,
    .data-form textarea.form-control.is-invalid:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.18) !important;
    }

    /* ============================
       ARRAY / SUB-ITEMS
       ============================ */
    .array-container {
      border: 1.5px solid var(--bs-border-color);
      border-radius: var(--ab-radius-md);
      padding: 1rem;
      margin-bottom: 1rem;
      background-color: var(--bs-tertiary-bg);
    }

    .array-item {
      background-color: var(--bs-body-bg);
      border: 1px solid var(--bs-border-color-subtle);
      border-radius: var(--ab-radius-sm);
      padding: 0.875rem;
      margin-top: 0.625rem;
      box-shadow: var(--ab-shadow-xs);
      position: relative;
    }

    .array-item .btn-remove-item {
      position: absolute;
      top: 0.625rem;
      right: 0.625rem;
      z-index: var(--ab-z-raised);
    }

    /* ============================
       PROPERTY GRID (compact inline)
       ============================ */
    .property-grid .form-grid {
      display: grid;
      gap: 0;
    }

    .property-grid .data-form-body {
      max-height: none !important;
      overflow: visible !important;
      padding: 0;
    }

    .property-grid .field-container {
      margin-bottom: 0;
      display: block;
    }

    .property-grid .field-label {
      font-size: var(--ab-text-xs);
      font-weight: var(--ab-weight-medium);
    }

    .property-grid input.form-control,
    .property-grid select.form-select,
    .property-grid textarea.form-control {
      font-size: var(--ab-text-sm);
      padding: 0.25rem 0.5rem;
      width: 100%;
      box-sizing: border-box;
    }

    /* ============================
       MOBILE FULLSCREEN OVERRIDES
       ============================ */
    @media (max-width: 767px) {
      /* Remove ALL height caps inside the slide-in form-container */
      .data-form {
        max-height: none !important;
      }

      /* Compact header on mobile */
      .card-header {
        padding: 0.875rem 1rem !important;
      }

      .card-header h5 {
        font-size: var(--ab-text-sm) !important;
      }

      /* Tighter body padding */
      .data-form-body {
        padding: 1rem !important;
      }

      /* Footer sticks to bottom */
      .card-footer {
        padding: 0.75rem 1rem !important;
        border-radius: 0 !important;
      }
    }
  `;


  async initialize() {
    this.log.info("Initializing Data Form", this.eventGroup);

    // --- MODIFICATION: Build the dialog structure but don't show it ---
    if (this.config.layout === 'dialog') {
      // Create the main container (backdrop) but don't attach it to the DOM yet.
      this.formContainer = document.createElement("div");
      this.formContainer.classList.add('dialog-backdrop');
      this.formContainer.style.display = 'none'; // Initially hidden
      this.formContainer.setAttribute('id', this.eventGroup);

      const dialogContainer = document.createElement("div");
      dialogContainer.classList.add('dialog-container');
      this.formContainer.appendChild(dialogContainer);
      this.dialogContainer = dialogContainer; // This is where the form will be rendered.

    } else {
      // For standard layouts, create the container as before.
      this.formContainer = document.createElement("div");
      this.formContainer.classList.add(...this.config.containerClass.split(' '));
      this.formContainer.setAttribute('id', this.eventGroup);
    }

    if (this.config.autoLoad && this.config.dataProvider) {
      await this.loadData();
    }
    if (!this.config.isDetailPane && this.config.autoLoad) {
      this.setupEventHandlers();
      await this.loadData();
    }
    this.applyStyles();
  }

  show() {
    if (this.config.layout !== 'dialog' || this.isModalVisible) {
      return;
    }
    // Attach the prepared container to the body and make it visible.
    document.body.appendChild(this.formContainer);
    this.formContainer.style.display = 'flex';
    this.isModalVisible = true;
    this.render(); // Re-render content inside the now-visible dialog
  }

  // --- NEW: Method to hide and remove the modal ---
  hide() {
    if (this.config.layout !== 'dialog' || !this.isModalVisible) {
      return;
    }
    // Remove the element from the DOM.
    if (this.formContainer && this.formContainer.parentElement) {
      this.formContainer.parentElement.removeChild(this.formContainer);
    }
    this.isModalVisible = false;
  }



  setupEventHandlers() {
    if (this.config.dataProvider) {
      this.config.dataProvider.on(`dataLoaded`,
        (data) => this.handleDataLoaded(data));
      this.config.dataProvider.on(`dataError`,
        (error) => this.handleDataError(error));
    }
  }

  async loadData() {
    try {
      this.loading = true;
      this.render();
      await this.config.dataProvider.loadData(this.config.context);
    } catch (error) {
      this.handleDataError(error);
    }
  }

  handleDataLoaded(data) {
    this.setData(data);
    this.loading = false;
    this.render();
  }

  handleDataError(error) {
    this.loading = false;
    this.error = error;
    this.render();
  }

  setExternalData(data) {
    this.externalData = { ...this.externalData, ...data };
  }

  async setData(data) {
    if (!data) {
      this.data = null;
      this.render();
      return;
    }
    this.data = data;
    this.isDirty = false;
    // Update custom components if already rendered
    for (const fieldName in this.config.fields) {
      const fieldConfig = this.config.fields[fieldName];
      if (
        fieldConfig.component &&
        this.fieldComponents[fieldName] &&
        typeof this.fieldComponents[fieldName].setValue === 'function'
      ) {
        const value = getValueByPath(data, fieldName);
        const field = this.fieldComponents[fieldName];
        field.setValue(value);
        if (typeof this.fieldComponents[fieldName].onParentFormUpdate === 'function') {
          field.onParentFormUpdate(data);
        }
      }
    }
    this.render();
    this.updateConditionalVisibility();

  }

  getDefaultData() {
    const clientConfig = this.appClient.sessionTokenHandler.getClientConfig();
    const defaultDataKeys = clientConfig?.dataform?.defaultDataKeys || [];

    const defaultData = {};

    for (const key of defaultDataKeys) {
      if (defaultData[key] === undefined) { // Check if the key is not already set
        const sharedValue = this.appClient.getSharedData(key);
        if (sharedValue !== undefined && sharedValue !== null) {
          defaultData[key] = sharedValue;
        }
      }
    }
    return defaultData;
  }

  getData() {
    if (this.config.mode === 'display') {
      return this.data;
    }

    const form = this.formElement;
    if (!form) {
      return null;
    }

    // --- START OF FIX: Use a clone of original data as the base ---
    // This ensures that fields not currently visible in the form (due to `visibleWhen`)
    // are still included in the final data object with their original values.
    // This prevents validation errors when the schema's `.refine()` expects those fields to exist.
    const finalData = JSON.parse(JSON.stringify(this.data || {}));
    const idField = this.config.idField || '_id';
    if (this.data && this.data[idField]) {
      finalData[idField] = this.data[idField];
    }
    // --- END OF FIX ---

    for (const fieldName in this.config.fields) {
      const fieldConfig = this.config.fields[fieldName];

      // Check visibility against the complete current state.
      const isVisible = typeof fieldConfig.visibleWhen !== 'function' || fieldConfig.visibleWhen(finalData);

      if (isVisible) {
        let fieldValue;
        if (fieldConfig.component || ['FileUpload', 'Location', 'Signature', 'TagInput'].includes(fieldConfig.type)) {
          fieldValue = this.fieldComponents[fieldName]?.getValue();
        } else if (fieldConfig.type === 'array') {
          fieldValue = this.getArrayFieldData(fieldName, fieldConfig);
        } else {
          const field = form.elements[`${this.config.eventGroup}-${fieldName}`];
          fieldValue = field ? this.getFieldValue(field) : undefined;
        }

        if (fieldValue !== undefined) {
          if (fieldName.includes('.')) {
            setValueByPath(finalData, fieldName, fieldValue);
          } else {
            finalData[fieldName] = fieldValue;
          }
        }
      }
    }

    const defaultData = this.getDefaultData();
    return {
      ...defaultData,
      ...this.externalData,
      ...finalData,
    };
  }


  // Helper for array fields
  getArrayFieldData(fieldName, fieldConfig) {
    const container = this.fieldComponents[fieldName];
    if (!container) return [];
    const items = Array.from(container.querySelectorAll('.array-item'));
    const result = items.map((item, index) => {
      let itemData = {};
      for (const subField in fieldConfig.item.fields) {
        const inputName = `${this.config.eventGroup}-${fieldName}[${index}].${subField}`;
        const input = item.querySelector(`[name="${inputName}"]`);
        itemData[subField] = input ? this.getFieldValue(input) : undefined;
      }
      return itemData;
    });
    return result;
  }

  getFieldValue(field) {
    switch (field.type) {
      case 'checkbox':
        return field.checked;
      case 'select-multiple':
        return Array.from(field.selectedOptions).map(opt => opt.value);
      case 'date':
        return field.value ? new Date(field.value) : null;
      case 'time':
        return field.value ? new Date(`1970-01-01T${field.value}${field.value.length === 5 ? ":00" : ""}`) : null;
      case 'datetime':
      case 'datetime-local':
        return field.value ? new Date(field.value) : null;
      case 'month':
        return field.value ? new Date(field.value + '-01') : null;
      case 'number':
      case 'range':
        return field.value ? Number(field.value) : null;
      default:
        return field.value;
    }
  }

  async validate() {
    if (!this.config.schema) return true;
    const data = this.getData();
    try {
      this.log.info('Validating data:', data);
      await this.config.schema.parseAsync(data);
      // Clear any previous errors if successful
      this.errors = {};
      this.isValid = true;
      // Optionally update error displays in place, if using separate elements
      this.updateErrorDisplays();
      return true;
    } catch (error) {
      this.log.error('Validation error details:', { error, issues: error.issues });
      this.errors = {};
      if (error.issues) {
        error.issues.forEach(issue => {
          const field = issue.path.join('.');
          this.errors[field] = issue.message;
        });
      }
      this.isValid = false;
      // Instead of re-rendering the entire form, update only error message elements.
      this.updateErrorDisplays(); // <-- New method that only updates error fields.
      return false;
    }
  }


  updateErrorDisplays() {
    for (const field in this.config.fields) {
      // Use the same id we set in renderField():
      const errorDiv = this.formElement.querySelector(`#${this.config.eventGroup}-${field}-error`);
      // Retrieve the input element by its name from the form:
      const inputEl = this.formElement.elements[`${this.config.eventGroup}-${field}`];
      const container = this.formElement.querySelector(`[data-field-name="${field}"]`);

      if (errorDiv) {
        if (this.errors[field]) {
          errorDiv.textContent = this.errors[field];
          if (inputEl) {
            inputEl.classList.add('is-invalid');
          }
          if (container) {
            container.classList.add('wiggle-error');
            setTimeout(() => {
              container.classList.remove('wiggle-error');
            }, 400);
          }
        } else {
          errorDiv.textContent = "";
          if (inputEl) {
            inputEl.classList.remove('is-invalid');
          }
        }
      }
    }
  }

  async renderField(fieldName, fieldConfig) {
    // Get the current value (support dot‑notation)
    let value = this.data ? getValueByPath(this.data, fieldName) : '';
    const container = document.createElement('div');
    container.classList.add('field-container');
    container.setAttribute('data-field-name', fieldName);
    if (typeof fieldConfig.visibleWhen === "function") {
      const visible = fieldConfig.visibleWhen(this.data || {});
      container.style.display = visible ? '' : 'none';
    }
    // If in display mode, use the display renderer
    if (this.config.mode === 'display') {
      if (fieldConfig.type === 'array') {
        return this.renderDisplayArrayField(fieldName, fieldConfig, value);
      }
      return this.renderDisplayField(fieldName, fieldConfig, value);
    }
    // For array fields, delegate to specialized array rendering.
    if (fieldConfig.type === 'array') {
      return this.renderArrayField(fieldName, fieldConfig, value);
    }
    // For non-boolean fields, add a label.
    if (fieldConfig.type !== 'boolean') {
      const label = document.createElement('label');
      label.classList.add('field-label');
      label.setAttribute('for', `${this.config.eventGroup}-${fieldName}`);
      label.textContent = fieldConfig.label;
      const isRequired = this.config.schema?._def?.shape?.[fieldName]?._def?.isRequired || false;
      if (isRequired) {
        label.innerHTML += '<span class="required-indicator">*</span>';
      }
      container.appendChild(label);
    }
    try {
      let control = await this.createFormControl(fieldName, fieldConfig, value);
      if (control) {
        container.appendChild(control);
      }
    } catch (error) {
      this.log.error(`Error rendering field ${fieldName}:`, error);
      const errorDiv = document.createElement('div');
      errorDiv.classList.add('field-error');
      errorDiv.textContent = `Error rendering field: ${fieldName}`;
      container.appendChild(errorDiv);
    }

    // Create and append an error container for this field.
    let errorDiv = document.createElement('div');
    errorDiv.classList.add('field-error');
    errorDiv.id = `${this.config.eventGroup}-${fieldName}-error`;
    container.appendChild(errorDiv);
    return container;
  }




  renderDisplayField(fieldName, fieldConfig, value) {
    const container = document.createElement('div');
    container.classList.add('field-container');
    const label = document.createElement('div');
    label.classList.add('field-label');
    label.textContent = fieldConfig.label;
    container.appendChild(label);
    const valueDiv = document.createElement('div');
    valueDiv.classList.add('field-value');
    if (fieldConfig.type === 'reference') {
      const displayValue = this.data && fieldName ?
        (this.data[`${fieldName}Display`] || value || '') : '';
      if (fieldConfig.render) {
        valueDiv.innerHTML = fieldConfig.render(value, {
          ...this.data,
          [`${fieldName}Display`]: displayValue
        });
      } else {
        valueDiv.innerHTML = displayValue || '<span class="text-muted">Not set</span>';
      }
    } else if (fieldConfig.render) {
      valueDiv.innerHTML = fieldConfig.render(value, this.data || {});
    } else {
      switch (fieldConfig.type) {
        case 'boolean':
          valueDiv.textContent = value ? 'Yes' : 'No';
          break;
        case 'date':
          valueDiv.textContent = value ? new Date(value).toLocaleDateString() : '';
          break;
        default:
          valueDiv.textContent = value || '';
      }
    }
    container.appendChild(valueDiv);
    return container;
  }

  //–––––– Array field rendering in edit mode ––––––
  async renderArrayField(fieldName, fieldConfig, value) {
    const container = document.createElement('div');
    container.classList.add('array-container');
    // Add an "Add" button.
    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.classList.add('btn', 'btn-sm', 'btn-outline-primary', 'd-inline-flex', 'align-items-center', 'gap-1');
    addButton.innerHTML = `<i class="bi bi-plus-lg"></i> <span>Add ${this.t(fieldConfig.label)}</span>`;
    addButton.setAttribute('data-i18n', `Add ${fieldConfig.label}`);
    addButton.addEventListener('click', () => {
      const index = container.querySelectorAll('.array-item').length;
      const newItemContainer = document.createElement('div');
      newItemContainer.classList.add('array-item');
      // Add a remove button.
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.classList.add('btn', 'btn-icon-chip', 'btn-outline-danger', 'btn-sm', 'btn-remove-item');
      removeButton.innerHTML = `<i class="bi bi-trash"></i>`;
      removeButton.setAttribute('title', this.t('Remove'));
      removeButton.addEventListener('click', () => {
        newItemContainer.remove();
        if (this.formElement) {
          this.formElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      newItemContainer.appendChild(removeButton);
      // Render each sub-field.
      for (const subField in fieldConfig.item.fields) {
        const subFieldConfig = fieldConfig.item.fields[subField];
        const fullFieldName = `${fieldName}[${index}].${subField}`;
        const subFieldContainer = document.createElement('div');
        subFieldContainer.classList.add('field-container');
        const label = document.createElement('label');
        label.classList.add('field-label');
        label.setAttribute('for', `${this.config.eventGroup}-${fullFieldName}`);
        label.textContent = subFieldConfig.label;
        subFieldContainer.appendChild(label);
        this.createFormControl(fullFieldName, subFieldConfig, undefined)
          .then(control => {
            subFieldContainer.appendChild(control);
            newItemContainer.appendChild(subFieldContainer);
          });
      }
      container.appendChild(newItemContainer);
    });
    container.appendChild(addButton);
    // Render existing items.
    const items = Array.isArray(value) ? value : [];
    for (let i = 0; i < items.length; i++) {
      const itemData = items[i];
      const itemContainer = document.createElement('div');
      itemContainer.classList.add('array-item');
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.classList.add('btn', 'btn-icon-chip', 'btn-outline-danger', 'btn-sm', 'btn-remove-item');
      removeButton.innerHTML = `<i class="bi bi-trash"></i>`;
      removeButton.setAttribute('title', this.t('Remove'));
      removeButton.addEventListener('click', () => {
        itemContainer.remove();
        if (this.formElement) {
          this.formElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      itemContainer.appendChild(removeButton);
      for (const subField in fieldConfig.item.fields) {
        const subFieldConfig = fieldConfig.item.fields[subField];
        const fullFieldName = `${fieldName}[${i}].${subField}`;
        const subValue = itemData ? itemData[subField] : undefined;
        const subFieldContainer = document.createElement('div');
        subFieldContainer.classList.add('field-container');
        const label = document.createElement('label');
        label.classList.add('field-label');
        label.setAttribute('for', `${this.config.eventGroup}-${fullFieldName}`);
        label.textContent = subFieldConfig.label;
        subFieldContainer.appendChild(label);
        const control = await this.createFormControl(fullFieldName, subFieldConfig, subValue);
        subFieldContainer.appendChild(control);
        itemContainer.appendChild(subFieldContainer);
      }
      container.appendChild(itemContainer);
    }
    this.fieldComponents[fieldName] = container;
    return container;
  }

  //–––––– Array fields in display mode ––––––
  async renderDisplayArrayField(fieldName, fieldConfig, value) {
    const container = document.createElement('div');
    container.classList.add('array-display-container');
    if (!Array.isArray(value) || value.length === 0) {
      container.innerHTML = '<span class="text-muted">No items</span>';
      return container;
    }
    for (let i = 0; i < value.length; i++) {
      const itemData = value[i];
      const itemContainer = document.createElement('div');
      itemContainer.classList.add('array-display-item');
      for (const subField in fieldConfig.item.fields) {
        const subFieldConfig = fieldConfig.item.fields[subField];
        const subValue = itemData[subField];
        const subContainer = document.createElement('div');
        subContainer.classList.add('field-container');
        const label = document.createElement('div');
        label.classList.add('field-label');
        label.textContent = subFieldConfig.label;
        subContainer.appendChild(label);
        const valueDiv = document.createElement('div');
        valueDiv.classList.add('field-value');
        valueDiv.textContent = (subValue !== undefined && subValue !== null) ? subValue : '';
        subContainer.appendChild(valueDiv);
        itemContainer.appendChild(subContainer);
      }
      container.appendChild(itemContainer);
    }
    return container;
  }

  //–––––– Create form control (supports custom components, reference fields, selects, etc.) ––––––
  async createFormControl(fieldName, config, value) {

    if ((config.type === 'select' || config.type === 'reference' || config.type === 'multiSelect') && config.optionsSourceRpc) {
      const select = document.createElement('select');
      select.id = `${this.config.eventGroup}-${fieldName}`;
      select.name = select.id;
      select.classList.add('form-select');
      if (config.type === 'multiSelect') {
        select.setAttribute('multiple', 'multiple');
      }

      // Initially, show a loading state
      select.innerHTML = `<option value="">${this.t('Loading...')}</option>`;
      select.disabled = true;

      // Asynchronously populate the options
      this._populateSelectOptionsFromRpc(select, config, value);

      return select;
    }


    if (config.component) {
      // Check if the component is already created
      if (!this.fieldComponents[fieldName]) {
        // If not, create and initialize it
        const componentType = typeof (config.component) === 'string' ? config.component : config.component.constructor.name;
        const componentInstance = await this.createManagedComponent(componentType, null, {
          appClient: this.appClient,
          instanceId: `${this.config.eventGroup}-${fieldName}`,
          ...config,
        });
        this.fieldComponents[fieldName] = componentInstance;
      }
      const el = this.fieldComponents[fieldName].render();
      this.fieldComponents[fieldName].setValue(value);
      return await el;
    }

    // For built-in custom types.
    if (config.type === 'FileUpload' ||
      config.type === 'Location' ||
      config.type === 'Signature') {
      if (!this.fieldComponents[fieldName]) {
        const customComponent = await this.createManagedComponent(config.type, null, {
          appClient: this.appClient,
          instanceId: `${this.config.eventGroup}-${fieldName}`,
          ...config,
        });
        await customComponent.initialize();
        this.fieldComponents[fieldName] = customComponent;
      }
      // Reuse the existing instance; update its value if needed.
      const el = this.fieldComponents[fieldName].render();
      this.fieldComponents[fieldName].setValue(value);
      return await el;
    }
    if (config.type === 'reference') {
      return await this.createReferenceField(fieldName, config, value);
    }
    if (config.type === 'select' || config.type === 'multiSelect') {
      const select = document.createElement('select');
      select.id = `${this.config.eventGroup}-${fieldName}`;
      select.name = select.id;
      select.classList.add('form-select');
      if (config.type === 'multiSelect') {
        select.setAttribute('multiple', 'multiple');
      }
      if (config.options) {
        const isRequired = this.config.schema?._def?.shape?.[fieldName]?._def?.isRequired ?? false;
        if (config.type === 'select' && !isRequired) {
          const emptyOption = document.createElement('option');
          emptyOption.value = '';
          emptyOption.textContent = '-- Select --';
          select.appendChild(emptyOption);
        }
        config.options.forEach(option => {
          const opt = document.createElement('option');
          opt.value = typeof option === 'object' ? option.value : option;
          opt.textContent = typeof option === 'object' ? option.label : option;
          if (value === opt.value) opt.selected = true;
          select.appendChild(opt);
        });
      }
      if (value !== undefined && value !== null) {
        select.value = value;
      }
      return select;
    }
    if (config.type === 'boolean' || config.type === 'checkbox') {
      const container = document.createElement('div');
      container.classList.add('form-check');
      const input = document.createElement('input');
      input.setAttribute('type', 'checkbox');
      input.id = `${this.config.eventGroup}-${fieldName}`;
      input.name = input.id;
      input.classList.add('form-check-input');
      input.checked = !!value;
      const boolLabel = document.createElement('label');
      boolLabel.classList.add('form-check-label');
      boolLabel.setAttribute('for', input.id);
      boolLabel.textContent = config.label;
      container.appendChild(input);
      container.appendChild(boolLabel);
      return container;
    }
    if (config.type === 'radio') {
      const container = document.createElement('div');
      container.id = `${this.config.eventGroup}-${fieldName}`;
      if (config.options) {
        config.options.forEach(option => {
          const radioContainer = document.createElement('div');
          radioContainer.classList.add('form-check');
          const input = document.createElement('input');
          input.setAttribute('type', 'radio');
          input.classList.add('form-check-input');
          input.name = `${this.config.eventGroup}-${fieldName}`;
          input.id = `${this.config.eventGroup}-${fieldName}-${option.value}`;
          input.value = option.value;
          if (value === option.value) {
            input.checked = true;
          }
          const label = document.createElement('label');
          label.classList.add('form-check-label');
          label.setAttribute('for', input.id);
          label.textContent = option.label || option.value;
          radioContainer.appendChild(input);
          radioContainer.appendChild(label);
          container.appendChild(radioContainer);
        });
      }
      return container;
    }
    return this.createStandardInput(fieldName, config, value);
  }

  createStandardInput(fieldName, config, value) {
    const elTag = config.type === 'textarea' ? 'textarea' : 'input';
    const element = document.createElement(elTag);
    element.id = `${this.config.eventGroup}-${fieldName}`;
    element.name = element.id;
    element.classList.add('form-control');
    if (elTag === 'input') {
      const validTypes = ["text", "password", "email", "url", "tel", "number", "date", "datetime", "datetime-local", "month", "week", "time", "color", "search", "range"];
      const inputType = validTypes.includes(config.type) ? config.type : "text";
      element.setAttribute("type", inputType);
    } else {
      if (config.type === 'textarea') {
        element.setAttribute("rows", config.rows ? config.rows : 2);
      }
    }
    if (config.placeholder) {
      element.setAttribute("placeholder", config.placeholder);
    }
    if (value !== undefined && value !== null) {
      if (config.type === 'date') {
        const dateValue = (value instanceof Date) ? value : new Date(value);
        element.value = !isNaN(dateValue.getTime()) ? dateValue.toISOString().split('T')[0] : "";
      } else if (config.type === 'time') {
        const dateValue = (value instanceof Date) ? value : new Date(value);
        let hours = dateValue.getHours().toString().padStart(2, '0');
        let minutes = dateValue.getMinutes().toString().padStart(2, '0');
        element.value = `${hours}:${minutes}`;
      } else if (config.type === 'datetime-local') {
        if (value instanceof Date) {
          let isoStr = value.toISOString();
          element.value = isoStr.substring(0, 16);
        } else {
          element.value = value;
        }
      } else if (config.type === 'month') {
        if (value instanceof Date) {
          element.value = value.toISOString().slice(0, 7);
        } else {
          element.value = value;
        }
      } else if (config.type === 'textarea' && typeof value === 'object') {
        // --- FIX: Stringify objects for textareas ---
        try {
          element.value = JSON.stringify(value, null, 2);
        } catch (e) {
          element.value = String(value);
        }
        // --- END FIX ---
      } else {
        element.value = value;
      }

    } else {
      element.value = "";
    }
    return element;
  }

  async createReferenceField(fieldName, fieldConfig, value) {
    const input = document.createElement('select');
    input.setAttribute('id', `${this.config.eventGroup}-${fieldName}`);
    input.setAttribute('name', `${this.config.eventGroup}-${fieldName}`);
    input.classList.add('form-select');
    try {
      if (this.config.dataProvider) {
        input.innerHTML = '<option value="">Loading...</option>';
        input.disabled = true;

        const options = await this.config.dataProvider.getReferenceFieldOptions(fieldName, this.data || {});
        input.innerHTML = '';
        input.disabled = false;
        const isRequired = this.config.schema?._def?.shape?.[fieldName]?._def?.isRequired;
        if (!isRequired) {
          const emptyOption = document.createElement('option');
          emptyOption.value = '';
          emptyOption.textContent = '-- Select --';
          input.appendChild(emptyOption);
        }
        options.forEach(option => {
          const opt = document.createElement('option');
          opt.value = option.value;
          opt.textContent = option.label;
          if (value === option.value) {
            opt.selected = true;
          }
          input.appendChild(opt);
        });
      }
    } catch (error) {
      this.log.error(`Error creating reference field ${fieldName}:`, error);
      input.innerHTML = '<option value="">Error loading options</option>';
      input.disabled = true;
    }
    input.value = (value !== undefined && value !== null) ? value : '';
    return input;
  }


  async _populateSelectOptionsFromRpc(selectElement, fieldConfig, currentValue) {
    const rpcConfig = fieldConfig.optionsSourceRpc;
    if (!rpcConfig || !rpcConfig.method) {
      selectElement.innerHTML = `<option value="">${this.t('Error: RPC not configured')}</option>`;
      return;
    }

    try {
      const rpcResult = await this.appClient.rpcClient.call(rpcConfig.method, rpcConfig.params || {});

      selectElement.innerHTML = ''; // Clear "Loading..."

      const options = Array.isArray(rpcResult) ? rpcResult : (rpcResult && Array.isArray(rpcResult.data)) ? rpcResult.data : [];

      // Determine if the field is required by checking the Zod schema
      const isRequired = this.schema?.shape?.[fieldConfig.name]?._def?.typeName !== 'ZodOptional';

      // Add a default empty option if the field is not required
      if (!isRequired) {
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = `-- ${this.t('Select')} --`;
        selectElement.appendChild(emptyOption);
      }

      // Populate with options from the now-guaranteed array
      (options || []).forEach(option => {
        const opt = document.createElement('option');
        opt.value = option[rpcConfig.valueField] || option.value;
        opt.textContent = option[rpcConfig.displayField] || option.label;
        selectElement.appendChild(opt);
      });

      // --- START OF THE FIX ---
      // If there's only one option AND the field is required, auto-select it
      // and programmatically trigger the change event to ensure state is updated.
      if (options.length === 1 && isRequired) {
        const singleOptionValue = options[0][rpcConfig.valueField] || options[0].value;
        currentValue = singleOptionValue; // Force the selection
        this.log.debug(`Auto-selecting the only required option for '${fieldConfig.name}': ${currentValue}`);

        // Defer the event dispatch slightly to ensure the DOM is fully updated
        setTimeout(() => {
          selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        }, 0);
      }
      // --- END OF THE FIX ---

      // Re-select the current value if it exists
      if (currentValue !== undefined && currentValue !== null) {
        selectElement.value = currentValue;
      }

      selectElement.disabled = false; // Enable the select

    } catch (error) {
      this.log.error(`Failed to fetch options for field '${fieldConfig.name}' from RPC '${rpcConfig.method}':`, error);
      selectElement.innerHTML = `<option value="">${this.t('Error loading options')}</option>`;
    }
  }


  async render() {
    try {
      if (this.config.layout === 'dialog' && !this.isModalVisible) {
        return this.formContainer;
      }

      const container = this.config.layout === 'dialog' ? this.dialogContainer : this.formContainer;
      container.innerHTML = ''; // Clear previous content from the correct container.

      container.classList.add(this.config.mode === 'display' ? 'display-mode' : 'edit-mode');
      if (this.config.layout === 'propertyGrid') {
        container.classList.add('property-grid');
      }
      const form = document.createElement(this.config.mode === 'display' ? 'div' : 'form');
      form.classList.add('data-form');
      this.formElement = form;
      if (!this.config.hideHeader) {
        const header = document.createElement('div');
        header.classList.add('card-header', 'd-flex', 'justify-content-between', 'align-items-center');
        const title = document.createElement('h5');
        title.classList.add('mb-0');

        const entity = this.config.entityName || 'Item';
        let headerTitle = '';

        if (this.config.title) {
          // If a specific title is provided, use it directly (for CrudField modals etc.)
          headerTitle = this.config.title;
        } else {
          // Otherwise, construct it from the mode and entity name
          switch (this.config.mode) {
            case 'filter':
              headerTitle = this.t('Filter');
              break;
            case 'edit':
              // Check if the data has an ID to determine if it's a new or existing item
              const idField = this.config.dataProvider?.idField || '_id';
              const isNew = !this.data || !this.data[idField];
              headerTitle = isNew ? `New ${entity}` : `Edit ${entity}`;
              break;
            case 'display':
              headerTitle = `${entity} Details`;
              break;
            default:
              headerTitle = entity;
          }
        }

        title.textContent = headerTitle;
        title.setAttribute("data-i18n", title.textContent);
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.classList.add('btn-close');
        closeButton.setAttribute('aria-label', 'Close');
        closeButton.setAttribute('title', 'Close');
        closeButton.addEventListener('click', () => {
          if (this.config.mode === 'filter') {
            this.emit(`cancel`);
          } else {
            this.handleClose();
          }
        });
        header.appendChild(title);
        header.appendChild(closeButton);
        form.appendChild(header);
      }
      const formBody = document.createElement('div');
      formBody.classList.add('data-form-body');
      if (this.loading) {
        formBody.innerHTML = this.renderLoading();
      } else if (this.error) {
        formBody.innerHTML = this.renderError();
        const backButton = formBody.querySelector('#global-error-back-button');
        if (backButton) {
          backButton.addEventListener('click', () => {
            this.error = null;
            this.render();
          });
        }
      } else {
        const fieldsContainer = document.createElement('div');
        fieldsContainer.classList.add('form-grid');
        fieldsContainer.style.setProperty('--form-columns', this.config.columns);
        if (this.config.sections) {
          this.config.sections.forEach(section => {
            const sectionDiv = document.createElement('div');
            sectionDiv.classList.add('form-section');
            const title = document.createElement('div');
            title.classList.add('form-section-title');
            title.textContent = section.title;
            sectionDiv.appendChild(title);
            section.fields.forEach(async fieldName => {
              const fieldConfig = this.config.fields[fieldName];
              if (fieldConfig) {
                try {
                  const fieldElement = await this.renderField(fieldName, fieldConfig);
                  if (fieldElement) {
                    sectionDiv.appendChild(fieldElement);
                  }
                } catch (error) {
                  this.log.error(`Error rendering field ${fieldName}:`, error);
                  const errorDiv = document.createElement('div');
                  errorDiv.classList.add('field-error');
                  errorDiv.textContent = `Error rendering field: ${fieldName}`;
                  sectionDiv.appendChild(errorDiv);
                }
              }
            });
            fieldsContainer.appendChild(sectionDiv);
          });
        } else {
          Object.entries(this.config.fields).forEach(async ([fieldName, fieldConfig]) => {
            try {
              const fieldElement = await this.renderField(fieldName, fieldConfig);
              if (fieldElement) {
                fieldsContainer.appendChild(fieldElement);
              }
            } catch (error) {
              this.log.error(`Error rendering field ${fieldName}:`, error);
              const errorDiv = document.createElement('div');
              errorDiv.classList.add('field-error');
              errorDiv.textContent = `Error rendering field: ${fieldName}`;
              fieldsContainer.appendChild(errorDiv);
            }
          });
        }
        formBody.appendChild(fieldsContainer);
      }
      form.appendChild(formBody);
      if (this.config.mode !== 'display' && this.config.actions) {
        const footer = document.createElement('div');
        footer.classList.add(...this.config.footerClass.split(' '));
        const buttonContainer = document.createElement('div');
        buttonContainer.classList.add('d-flex', 'justify-content-end', 'gap-2');
        if (this.config.actions.save) {
          const saveButton = document.createElement('button');
          saveButton.type = 'button';
          saveButton.classList.add('btn', 'btn-primary');
          saveButton.textContent = this.config.mode === 'filter' ? this.t('Apply') : this.t('Save');
          saveButton.setAttribute("data-i18n", this.config.mode === 'filter' ? 'Apply' : 'Save');

          saveButton.addEventListener('click', async () => {
            const data = this.getData();
            if (this.config.mode === 'filter') {
              this.emit(`dataSaved`, data);
            } else {
              await this.handleSave();
            }
          });
          buttonContainer.appendChild(saveButton);
        }
        if (this.config.actions.cancel) {
          const cancelButton = document.createElement('button');
          cancelButton.type = 'button';
          cancelButton.classList.add('btn', 'btn-secondary');
          cancelButton.textContent = this.t('Cancel');
          cancelButton.setAttribute("data-i18n", 'Cancel');
          cancelButton.addEventListener('click', () => {
            if (this.config.mode === 'filter') {
              this.emit(`cancel`);
            } else {
              this.handleCancel();
            }
          });
          buttonContainer.appendChild(cancelButton);
        }
        footer.appendChild(buttonContainer);
        form.appendChild(footer);
      }
      container.appendChild(form);
      // Attach generic change and input event listeners to update conditional visibility & validate on change.
      this.attachFormEventListeners();

    } catch (error) {
      this.log.error('Error rendering form:', error);
      this.formContainer.innerHTML = this.renderError();
    }

    setTimeout(() => {
      this.appClient.tx.applyTranslations(this.formContainer);
    }, 0);

    return this.formContainer;
  }


  //–––––– Attach event listeners on the form for dynamic field visibility and on-change validation ––––––
  attachFormEventListeners() {
    if (!this.formElement) return;
    // Use input and change events
    this.formElement.addEventListener('input', this._debouncedHandleFormChange);
    this.formElement.addEventListener('change', this._debouncedHandleFormChange);
  }

  // Generic handler for form changes: update internal data, conditional visibility and validate if required.
  _handleFormChange(event) {
    // Update our internal data with the latest form state.
    this.data = this.getData();
    this.updateConditionalVisibility();

    const changedFieldName = event.target.name.replace(`${this.config.eventGroup}-`, '');
    for (const fieldName in this.config.fields) {
      const fieldConfig = this.config.fields[fieldName];
      if (Array.isArray(fieldConfig.dependsOn) && fieldConfig.dependsOn.includes(changedFieldName)) {
        // This field depends on the one that just changed. We need to re-render it.
        const fieldContainer = this.formElement.querySelector(`[data-field-name="${fieldName}"]`);
        if (fieldContainer) {
          // Re-render the field to fetch new options
          this.renderField(fieldName, fieldConfig).then(newFieldElement => {
            fieldContainer.replaceWith(newFieldElement);
          });
        }
      }
    }

    if (this.config.validation && this.config.validation.validateOnChange) {
      // Optionally, you could debounce this call if needed.
      this.validate();
    }


    Object.entries(this.fieldComponents).forEach(([fieldName, componentInstance]) => {
      if (typeof componentInstance.onParentFormUpdate === 'function') {
        componentInstance.onParentFormUpdate(this.data);
      }
    });
  }

  // Loop through fields with visibleWhen and update their container display style.
  updateConditionalVisibility() {
    const currentData = this.data || {};

    Object.keys(this.config.fields).forEach((fieldName) => {
      const fieldConfig = this.config.fields[fieldName];
      if (typeof fieldConfig.visibleWhen === 'function') {
        const container = this.formElement.querySelector(`[data-field-name="${fieldName}"]`);
        if (container) {
          container.style.display = fieldConfig.visibleWhen(currentData) ? '' : 'none';
        }
      }
    });
  }


  renderLoading() {
    return `
      <div class="text-center p-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="mt-2">Loading...</div>
      </div>
    `;
  }

  renderError() {
    return `
      <div class="text-center p-4 text-danger">
        <i class="bi bi-exclamation-circle display-4"></i>
        <div class="mt-2">${this.error?.message || 'An error occurred'}</div>
        <button id="global-error-back-button" type="button" class="btn btn-secondary mt-3">Back to Form</button>
      </div>
    `;
  }


  async handleSave() {
    try {
      const data = this.getData();
      this.log.info('Data before validation:', data);
      if (await this.validate()) {

        // --- START MODIFICATION 2: Respect dataProvider.idField and handle returned data ---
        const idField = this.config.dataProvider?.idField || '_id';
        const hasId = data[idField] !== undefined && data[idField] !== null && data[idField] !== '';

        if (hasId) {
          if (this.config.dataProvider && this.config.autoSave) {
            // The update method might return the updated record.
            const updatedData = await this.config.dataProvider.update(data);
            this.emit(`dataUpdated`, updatedData || data);
          } else {
            this.emit(`dataUpdated`, data);
          }
        } else {
          if (this.config.dataProvider && this.config.autoSave) {
            // The insert method often returns the newly created record with its ID.
            const savedData = await this.config.dataProvider.insert(data);
            this.emit(`dataSaved`, savedData || data);
          } else {
            this.emit(`dataSaved`, data);
          }
        }
        // --- END MODIFICATION 2 ---

      } else {
        Object.entries(this.errors).forEach(([field, errors]) => {
          this.log.error(`Validation error for ${field}:`, errors);
        });
      }
    } catch (error) {
      this.log.error('Error saving data:', error);
      if (error.message) {
        if (error.fields) {
          this.errors = error.fields;
          this.isValid = false;
          this.render();
        } else {
          this.handleDataError(error);
        }
      } else {
        this.handleDataError(error);
      }
    }
  }

  handleCancel() {
    this.emit(`cancel`);
  }

  handleClose() {
    if (this.config.layout === 'dialog') {
      this.hide();
    }
    this.emit(`cancel`);
  }


  clear() {
    this.data = null;
    this.errors = {};
    this.isDirty = false;
    this.isValid = true;
    this.render();
  }

  destroy() {
    this.log.info(`Destroying DataForm: ${this.config.instanceId}`);

    // --- MODIFICATION: Ensure the modal is hidden and removed on destroy ---
    if (this.isModalVisible) {
      this.hide();
    }

    if (this.formContainer) {
      this.formContainer.remove();
      this.formContainer = null;
    }

    // Unbind specific event listeners
    if (this.formElement) {
      this.formElement.removeEventListener('input', this._debouncedHandleFormChange);
      this.formElement.removeEventListener('change', this._debouncedHandleFormChange);
      this.formElement = null;
    }

    // Destroy managed child components
    Object.values(this.fieldComponents).forEach(component => {
      if (component && typeof component.destroy === 'function') {
        component.destroy();
      }
    });
    this.fieldComponents = {};

    this.emit('destroyed');
    super.destroy(); // Handles general event manager cleanup
  }

  static fromZodSchema(zodSchema, additionalConfig = {}) {
    // Generate field definitions from the provided Zod schema.
    const fields = DataForm.generateFieldsFromZod(zodSchema);
    // Merge the DataForm defaults, any extra config and add the generated fields and original schema.
    return {
      ...DataForm.getDefaultConfig(),
      ...additionalConfig,
      fields,
      schema: zodSchema
    };
  }

  static generateFieldsFromZod(schema, prefix = "") {
    let fields = {};
    // Process if the schema is an object
    if (schema._def.typeName === "ZodObject") {
      const shape = schema._def.shape();
      for (const key in shape) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const subSchema = shape[key];
        const subType = subSchema._def.typeName;
        if (subType === "ZodObject") {
          // Flatten nested objects using dot‑notation.
          Object.assign(fields, DataForm.generateFieldsFromZod(subSchema, fullKey));
        } else if (subType === "ZodArray") {
          // For arrays, check if items are objects or primitives.
          const itemSchema = subSchema._def.type;
          if (itemSchema._def.typeName === "ZodObject") {
            const itemFields = DataForm.generateFieldsFromZod(itemSchema);
            fields[fullKey] = {
              label: DataForm.capitalize(key),
              type: "array",
              item: { fields: itemFields }
            };
          } else {
            // Array of primitive types.
            fields[fullKey] = DataForm.buildFieldConfig(subSchema, key);
          }
        } else {
          // For primitives we generate a standard field config.
          fields[fullKey] = DataForm.buildFieldConfig(subSchema, key);
        }
      }
    } else {
      // If the provided schema is not an object, treat it as a single field.
      fields[prefix] = DataForm.buildFieldConfig(schema, prefix);
    }
    return fields;
  }

  static buildFieldConfig(schema, fieldName) {
    const typeName = schema._def.typeName;
    switch (typeName) {
      case "ZodString":
        return { label: DataForm.capitalize(fieldName), type: "text" };
      case "ZodNumber":
        return { label: DataForm.capitalize(fieldName), type: "number" };
      case "ZodBoolean":
        return { label: DataForm.capitalize(fieldName), type: "checkbox" };
      case "ZodDate":
        return { label: DataForm.capitalize(fieldName), type: "date" };
      case "ZodEnum":
        return { label: DataForm.capitalize(fieldName), type: "select", options: schema._def.values };
      case "ZodArray": {
        const itemSchema = schema._def.type;
        if (itemSchema._def.typeName === "ZodObject") {
          const itemFields = DataForm.generateFieldsFromZod(itemSchema);
          return { label: DataForm.capitalize(fieldName), type: "array", item: { fields: itemFields } };
        } else {
          const itemFieldConfig = DataForm.buildFieldConfig(itemSchema, fieldName + " item");
          return { label: DataForm.capitalize(fieldName), type: "array", item: { fields: { value: itemFieldConfig } } };
        }
      }
      default:
        return { label: DataForm.capitalize(fieldName), type: "text" };
    }
  }

  static capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}